import { useCallback, useEffect, useMemo, useState } from "react";
import { History, User } from "lucide-react";
import api from "@/lib/api";
import { DateInput } from "@/components/ui/date-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TAB_OPTIONS = [
  ["attendance", "الحضور والغياب"], ["inquiries", "المساءلات"], ["circulars", "التعاميم"],
  ["reports", "التقارير"], ["employees", "الموظفون"], ["performance", "تقييم الأداء"],
];

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString("ar-SA-u-nu-latn", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
};

export default function AuditLogTab() {
  const [rows, setRows] = useState(null);
  const [actor, setActor] = useState("all");
  const [tab, setTab] = useState("all");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (actor !== "all") p.set("actor_id", actor);
    if (tab !== "all") p.set("tab", tab);
    if (frm) p.set("frm", frm);
    if (to) p.set("to", to);
    api.get(`/audit-log?${p}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [actor, tab, frm, to]);

  useEffect(() => { load(); }, [load]);

  const actors = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => m.set(r.actor_id, r.actor_name));
    return [...m.entries()];
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="bg-white rounded-2xl border p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center shrink-0">
          <History className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h2 className="font-extrabold text-emerald-950 text-base">سجل التدقيق</h2>
          <p className="text-xs text-slate-500">
            كل إجراء ينفذه الموظفون المفوَّضون في التبويبات — من نفّذ وماذا فعل ومتى
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-3 flex flex-wrap gap-2">
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="w-full sm:w-56 h-10 text-xs bg-white" data-testid="audit-filter-actor">
            <SelectValue placeholder="كل الموظفين المفوَّضين" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الموظفين المفوَّضين</SelectItem>
            {actors.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tab} onValueChange={setTab}>
          <SelectTrigger className="w-full sm:w-48 h-10 text-xs bg-white" data-testid="audit-filter-tab">
            <SelectValue placeholder="كل التبويبات" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل التبويبات</SelectItem>
            {TAB_OPTIONS.map(([v, lb]) => <SelectItem key={v} value={v}>{lb}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateInput type="date" value={frm} onChange={(e) => setFrm(e.target.value)}
          data-testid="audit-filter-from"
          className="h-10 rounded-xl border border-slate-200 px-3 text-xs bg-white" />
        <DateInput type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="audit-filter-to"
          className="h-10 rounded-xl border border-slate-200 px-3 text-xs bg-white" />
      </div>

      {rows === null && <p className="text-center text-sm text-slate-400 py-10">جارٍ التحميل...</p>}
      {rows?.length === 0 && (
        <div className="bg-white rounded-2xl border p-10 text-center space-y-2" data-testid="audit-empty">
          <History className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="text-sm font-bold text-slate-500">لا توجد إجراءات مسجلة بعد</p>
          <p className="text-xs text-slate-400">ستظهر هنا إجراءات الموظفين المفوَّضين فور تنفيذها</p>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-emerald-950 text-white text-xs">
                  <th className="px-4 py-3 text-right font-semibold">التاريخ والوقت</th>
                  <th className="px-4 py-3 text-right font-semibold">الموظف المفوَّض</th>
                  <th className="px-4 py-3 text-right font-semibold">التبويب</th>
                  <th className="px-4 py-3 text-right font-semibold">الإجراء</th>
                  <th className="px-4 py-3 text-right font-semibold">المسار</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap" dir="ltr">{fmtWhen(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-xs font-bold text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-emerald-800" />{r.actor_name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] font-bold rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-800 border-emerald-200">
                        {r.tab_label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">{r.action}</td>
                    <td className="px-4 py-2.5 text-[10px] text-slate-400" dir="ltr">
                      <span className="font-mono">{r.method} {r.path}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
